import { BaseZenputCollection, BaseZenputModel, Paginator } from '../shared/forms';
import { SensorIntegration, SensorRelatedAccount } from '../shared/sensors/models';
import { sanitize_dev_eui } from '../sensors/sensors';

export const OperatorIds = {
    NOT_BETWEEN: 'not_between',
    BETWEEN: 'between',
    LESS_THAN: '<',
    LESS_THAN_EQUAL: '<=',
    GREATER_THAN: '>',
    GREATER_THAN_EQUAL: '>=',
    EQUAL: '=',
    NOT_EQUAL: '!='
};

export const SensorReading = BaseZenputModel.extend({
    defaults: {
        value: null,
        timestamp: null
    }
});

export const Gateway = BaseZenputModel.extend({
    defaults: {
        id: null,
        name: null,
        gateway_type: null,
        battery_level: null,
        is_online: null,
        external_key: null,
        secondary_key: null,
        account: null,
        integration: null,
        manufacturer: null
    },

    submodels: {
        account: SensorRelatedAccount,
        integration: SensorIntegration
    },

    account: null, // Account model

    url: function () {
        return `/api/v2/sensors/gateways/${this.account.id}/`;
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.account) {
            this.account = $f.mobile.dashboard.get_active_account_or_group();
        }
    },

    update_barcode_value: function (barcode, manufacturer) {
        if (manufacturer === $f.mobile.sensors.util.MANUFACTURER.MONNIT) {
            this.set({
                ...$f.mobile.sensors.util.get_gateway_ids_from_scanner(barcode),
                manufacturer
            });
        } else {
            const is_sensor = false;
            this.set({
                ...$f.mobile.sensors.util.get_info_from_lorawan_barcode(barcode, is_sensor),
                manufacturer
            });
        }
    },

    set_device_id: function (value) {
        this.set('external_key', value);
    },

    get_device_id: function () {
        return this.get('external_key');
    },

    sanitize_device_id: function () {
        const device_id = this.get_device_id();
        this.set_device_id(sanitize_dev_eui(device_id.toUpperCase()));
    }
});

export const Gateways = BaseZenputCollection.extend({
    model: Gateway,
    account: null, // Account model

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.account) {
            this.account = $f.mobile.dashboard.get_active_account_or_group();
        }
    },

    url: function () {
        return `/api/v2/sensors/gateways/${this.account.id}/`;
    }
});

export const CooldownPaginator = Paginator.extend({
    defaults: $.extend({}, Paginator.prototype.defaults, {
        order_by: '-date_started',
        limit: 20 // This limit matches the backend DRF endpoint default.
    }),

    override_default_query_string_attrs: ['start', 'limit', 'order_by', 'account_id'],

    initialize: function () {
        // override the additions of unnecessary query strings from the base class.
        CooldownPaginator.__super__.initialize.apply(this, arguments);
        this.default_query_string_attrs = this.override_default_query_string_attrs;
    }
});

export const CooldownSensor = BaseZenputModel.extend({
    defaults: {
        id: null,
        name: null
    }
});

export const CooldownStage = BaseZenputModel.extend({
    defaults: {
        id: null,
        date_processed: null,
        date_reading: null,
        is_active: true,
        operator_id: null,
        reading: null,
        sensor_reading: null,
        status: null
    },

    get_stage_number: function () {
        var STAGE_NUMBER_OFFSET = 1;
        return this.collection.indexOf(this) + STAGE_NUMBER_OFFSET;
    },

    get_stage_title: function () {
        return `${_t('mobile_sensors_cooldown_stage')} ${this.get_stage_number()}`;
    },

    get_date_reading: function () {
        return $f.shared.sensors.util.get_formatted_date_for_cooldown(
            this.get('date_reading'),
            $f.mobile.dashboard.get_active_account_or_group(),
            $f.utils.TIME_FMT
        );
    },

    get_duration: function (cooldown) {
        if (!cooldown) {
            return;
        }

        return $f.mobile.sensors.util.get_duration_between_dates(
            cooldown.get('date_started'),
            this.get('date_reading')
        );
    }
});

export const CooldownStages = BaseZenputCollection.extend({
    model: CooldownStage
});

export const CooldownForTask = BaseZenputModel.extend({
    defaults: {
        id: null,
        food: null,
        stages: null
    },

    submodels: {
        stages: CooldownStages
    }
});

export const Cooldown = BaseZenputModel.extend({
    defaults: {
        id: null,
        name: null,
        food: null,
        status: null,
        account: null,
        sensor: null,
        date_started: null,
        date_endded: null,
        stages: null
    },

    submodels: {
        account: SensorRelatedAccount,
        sensor: CooldownSensor,
        stages: CooldownStages
    },

    validation: {
        food: {
            minLength: 1,
            maxLength: 255,
            msg: function () {
                return _t('mobile_sensors_cooldown_please_enter_food');
            },
            required: true
        },
        sensor: {
            required: true,
            msg: function () {
                return _t('mobile_sensors_cooldown_please_select_sensor');
            }
        }
    },

    url: function () {
        return '/api/v2/sensors/cooldowns/';
    },

    get_cooldown_status_for_display: function () {
        var statuses = {
            in_progress: _t('mobile_sensors_cooldown_status_in_progress'),
            failed: _t('mobile_sensors_cooldown_status_failed'),
            success: _t('mobile_sensors_cooldown_status_success')
        };

        return statuses[this.get('status')];
    },

    get_date_started: function () {
        return $f.shared.sensors.util.get_formatted_date_for_cooldown(
            this.get('date_started'),
            this.get('account'),
            $f.utils.DATE_AND_TIME_FMT
        );
    },

    get_date_ended: function () {
        return $f.shared.sensors.util.get_formatted_date_for_cooldown(
            this.get('date_ended'),
            this.get('account'),
            $f.utils.DATE_AND_TIME_FMT
        );
    },

    get_duration: function () {
        return $f.mobile.sensors.util.get_duration_between_dates(this.get('date_started'), this.get('date_ended'));
    },

    get_status_title: function () {
        if (this.get('status') === $f.mobile.sensors.util.COOLDOWN_STATUS_FAILED) {
            return _t('mobile_sensors_cooldown_failed_cooldown_process');
        }

        return _t('mobile_sensors_cooldown_cooldown_successful');
    },

    get_failing_reading: function () {
        var stages = this.get('stages');
        if (this.get('status') === $f.mobile.sensors.util.COOLDOWN_STATUS_SUCCESS || stages.length === 0) {
            return;
        }

        return stages.last().get('sensor_reading');
    }
});

const SensorTrigger = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.SensorTrigger',

    defaults: {
        id: null,
        title: null,
        temperature_criterion: null
    }
});

export const SensorTriggers = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.sensors.models.SensorTriggers',

    plot_line_colors: ['#F97A22', '#44516B', '#E9960C', '#009B98', '#5349EF'],
    plot_band_colors: ['#FFDFB5', '#CAD5E8', '#F7D388', '#B3E3E3', '#CCCAFF'],

    color_opacity: 'B3',

    model: SensorTrigger,

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
    },

    _getSensorTriggerColor: function (index) {
        const rawPlotBandColor = this.plot_band_colors[index % this.plot_band_colors.length];
        const rawPlotLineColor = this.plot_line_colors[index % this.plot_line_colors.length];
        return {
            plotBandColor: `${rawPlotBandColor}${this.color_opacity}`,
            plotLineColor: `${rawPlotLineColor}${this.color_opacity}`
        };
    },

    filterOutEqual: function () {
        return this.filter((trigger) => {
            const criterion = trigger.get('temperature_criterion');
            const operatorId = criterion.operator_id;

            return operatorId !== OperatorIds.EQUAL && operatorId !== OperatorIds.NOT_EQUAL;
        });
    },

    getSensorTriggerColors: function () {
        const models = this.filterOutEqual();
        return models.reduce(
            (acc, trigger, index) => ({
                ...acc,
                [trigger.get('id')]: this._getSensorTriggerColor(index)
            }),
            {}
        );
    },

    getTriggersWithColors: function (settingTriggers) {
        const triggerColors = this.getSensorTriggerColors();

        return this.filterOutEqual().reduce((acc, trigger) => {
            const settingTrigger = settingTriggers.findWhere({ id: trigger.get('id') });

            if (!settingTrigger.get('active')) {
                return acc;
            }

            const triggerWithColor = new TriggerWithColor({
                title: trigger.get('title'),
                temperatureCriterion: trigger.get('temperature_criterion'),
                id: trigger.get('id'),
                ...triggerColors[trigger.get('id')]
            });

            return [...acc, triggerWithColor];
        }, []);
    },

    url: function () {
        return `/api/v2/sensors/triggers/${this.id}/`;
    }
});

export const SettingTrigger = BaseZenputModel.extend({
    defaults: {
        id: null,
        title: null,
        active: true
    }
});

export const SettingTriggers = BaseZenputCollection.extend({
    model: SettingTrigger
});

const TriggerWithColor = BaseZenputModel.extend({
    defaults: {
        id: null,
        title: null,
        temperatureCriterion: null,
        plotLineColor: null,
        plotBandColor: null
    }
});

export const TriggersWithColors = BaseZenputCollection.extend({
    model: TriggerWithColor
});

export const Cooldowns = BaseZenputCollection.extend({
    model: Cooldown,

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new CooldownPaginator();
        }
    },

    url: function () {
        return `/api/v2/sensors/cooldowns/?${this.paginator.get_query_string()}`;
    }
});

export const ExportSensorTempLogReport = BaseZenputModel.extend({
    defaults: {
        location_id: null,
        recipient_email: null,
        date_start: null
    },

    url: function () {
        const paginator = new Paginator();
        const recipient_email = this.get('recipient_email');
        const date_start = this.get('date_start');

        if (recipient_email) {
            paginator.set('recipient_email', recipient_email);
        }

        if (date_start) {
            paginator.set('date_start', date_start);
        }

        const query_params = paginator.get_query_string({ attrs: ['recipient_email', 'date_start'] });

        return `/api/v2/sensors/export/location/${this.get('location_id')}/sensor_temp_log_report/?${query_params}`;
    }
});
